/* eslint-disable @typescript-eslint/no-explicit-any */

import React, {
  useState, useMemo, useEffect, FC,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Alert,
  Button,
} from '@mui/material';
import StyledBox, {
  StyledIconedBox,
  StyledFormGroup,
} from 'views/rebranded-module/GeoViewV2/components/GeoWidgetSection/GeoWidgetSection.styles';
import {
  FilterAutoComplete,
  GeoLocationField,
  GeoRangePicker,
} from 'views/rebranded-module/GeoViewV2/components';
import {
  getDayjsDate,
  formatDateToResponse,
} from 'views/rebranded-module/GeoViewV2/utils/dateHandlers';
import {
  PICKUP_LOCATION,
  DROPOFF_LOCATION,
  FILTERS,
} from 'views/rebranded-module/GeoViewV2/utils/constants';
import axios, { API } from 'lib/config/axios';
import dynamic from 'next/dynamic';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { setSnackbar } from 'redux/features/ui-slice';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { mapNamedLocaleToLocale } from 'lib/data/localesMap';
import { StyledTooltip } from 'views/rebranded-module/GeoViewV2/components/GeoRangePicker/GeoRangePicker.styles';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import { RiMapPin2Line, RiMapPinRangeLine, RiCalendar2Line } from 'react-icons/ri';

const GeoWidgetDialog = dynamic(() => import('views/rebranded-module/GeoViewV2/components/GeoWidgetDialog').then(
  (file) => file.GeoWidgetDialog,
));
const GeoFilterDialog = dynamic(() => import('views/rebranded-module/GeoViewV2/components/GeoFilterDialog').then(
  (file) => file.GeoFilterDialog,
));

const { VEHICLE_MODELS, VEHICLE_MAKES, VEHICLE_TYPES } = FILTERS;

let controller;
let timeoutId;
const GeoWidgetSection:FC<any> = (props) => {
  const {
    rentalData,
    handleFieldChange,
    openFilterDialog,
    handleClearVehicleFilters,
    handleFilterDialogClose,
    widgetRef,
  } = props;
  const cmsData = useContent('GeoPage');
  const appNavigator = useAppNavigator();
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sameDropoff, setSameDropoff] = useState(true);
  const [geoWidgetDialog, setGeoWidgetDialog] = useState(false);

  const [locations, setLocations] = useState({
    pickup: [],
    dropoff: [],
  });
  const [loadingLocations, setLoadingLocations] = useState(false);
  const hasNoDropoffLocations = useMemo(
    () => locations.dropoff?.length === 0,
    [locations?.dropoff],
  );
  const getObjectValue = (filterType, selectedFilter) => rentalData?.filters[filterType].find(
    (filter) => filter.id === selectedFilter?.id,
  )
    || selectedFilter
    || null;
  const getCurrentPosition = () => {
    if ('navigator' in window) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        });
      });
    }
    return null;
  };
  const getDropoffLocatoins = (params) => axios
    .get(API.nearbyDropoffLocations, {
      params,
    })
    .then((res) => {
      setLocations((prevData) => ({
        ...prevData,
        dropoff: res.data.nearby_dropoff_locations,
      }));
    })
    .catch((err) => {
      if (err.message !== 'canceled') {
        handleErrorResponse(err);
      }
    });
  const getPickupLocations = (query) => {
    setLoadingLocations(true);
    axios
      .get(API.pickupPlaces, {
        signal: controller.signal,
        params: {
          q: query,
        },
      })
      .then((res) => {
        setLocations((prevData) => ({
          ...prevData,
          pickup: res.data.pickup_places,
        }));
        setLoadingLocations(false);
      })
      .catch((err) => {
        if (err.message !== 'canceled') {
          handleErrorResponse(err);
        }
      });
  };
  const handlePickupInputChange = (e, newValue, reason) => {
    if (reason === 'reset') return;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (newValue?.length > 2) {
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        getPickupLocations(newValue);
      }, 100);
    }
  };
  const handleReturnToDifferentLocationClick = (e, checked) => {
    setSameDropoff(checked);
    const params: { lat?: number; lng?: number; place_id?: number | null } = {};

    if (rentalData?.selected_filters.pickup_place?.id === null) {
      params.lat = rentalData?.selected_filters.pickup_place?.location?.lat;
      params.lng = rentalData?.selected_filters.pickup_place?.location?.lng;
    } else {
      params.place_id = rentalData?.selected_filters.pickup_place?.id;
    }
  };
  const handlePickupAutoCompleteChange = (e, newValue) => {
    if (newValue?.id === null) {
      return getCurrentPosition()
        .then((data) => {
          const { latitude: lat, longitude: lng } = data.coords;
          handleFieldChange({
            lat,
            lng,
            radius: null,
            pickup_place_slug: null,
            page: 1,
          });
          getDropoffLocatoins({ lat, lng });
        })
        .catch((error) => {
          switch (error.code) {
            case 1:
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: cmsData.locationBlockedMsg,
                  duration: 10000,
                }),
              );
              break;
            default:
              handleErrorResponse(error);
          }
        });
    }
    handleFieldChange({
      pickup_place_slug: newValue?.slug,
      pickup_place: newValue,
      page: 1,
      lat: null,
      lng: null,
      radius: null,
    });

    getDropoffLocatoins({ place_id: newValue?.id });

    return null;
  };
  const handleDropoffAutoCompleteChange = (e, newValue) => {
    handleFieldChange({
      dropoff_location_id: newValue?.id,
      page: 1,
    });
  };
  const handleDropoffFieldClear = () => {
    setSameDropoff(true);
    handleFieldChange({
      dropoff_location_id: null,
    });
  };
  const handleGeoWidgetDialogClose = () => {
    setGeoWidgetDialog(false);
  };
  const handleGeoWidgetOpen = () => {
    setGeoWidgetDialog(true);
  };

  const pickupLocations = useMemo(() => [
    { id: null, name: cmsData.useCurrentLocationText },
    ...(locations.pickup || []),
  ], [locations.pickup, cmsData]);

  const pickupLocation = useMemo(() => {
    const pickupLoc = pickupLocations.find(
      (location) => location.id === rentalData?.selected_filters.pickup_place?.id,
    );
    if (pickupLoc) {
      pickupLoc.display_name = rentalData?.selected_filters.pickup_place.name;
      return pickupLoc;
    }
    return null;
  }, [locations, rentalData]);
  const dropoffLocation = useMemo(() => {
    const dropoffLoc = locations.dropoff.find(
      (location) => location.id === rentalData?.selected_filters.dropoff_location?.id,
    );
    if (dropoffLoc) {
      dropoffLoc.display_name = rentalData?.selected_filters.dropoff_location?.name;
      return dropoffLoc;
    }
    return null;
  }, [locations, rentalData]);
  const pickupLabel = useMemo(() => {
    if (sameDropoff) return cmsData.pickupAndDropoffLabel;
    return cmsData.pickupLabel;
  }, [sameDropoff]);

  const pickupFieldProps = () => ({
    placeholder: cmsData.searchDestinationsLabel,
    fieldType: PICKUP_LOCATION,
    icon: RiMapPin2Line,
    mainColor: true,
    onInputChange: handlePickupInputChange,
    options: loadingLocations ? [] : pickupLocations,
    filterOptions: (x) => x,
    getOptionLabel: (option) => option.display_name || option.name,
    fullWidthMenu: !rentalData?.selected_filters.pickup_place,
    loading: loadingLocations,
    value: pickupLocation,
    label: pickupLabel,
    onChange: handlePickupAutoCompleteChange,
  });
  const dropoffFieldProps = () => ({
    placeholder: cmsData.dropoffLocationLabel,
    fieldType: DROPOFF_LOCATION,
    icon: isMobile ? RiMapPin2Line : RiMapPinRangeLine,
    mainColor: true,
    options: locations.dropoff,
    value: dropoffLocation,
    label: cmsData.dropoffLabel,
    getOptionLabel: (option) => option.display_name || option.name,
    onChange: handleDropoffAutoCompleteChange,
    handleClearRangePicker: handleDropoffFieldClear,
  });
  const returnToSameLocationProps = () => ({
    control: <Checkbox disabled={hasNoDropoffLocations} defaultChecked />,
    label: cmsData.returnToSameLabel,
    checked: sameDropoff,
    onChange: handleReturnToDifferentLocationClick,
  });

  const geoPickerValue = useMemo(
    () => [
      getDayjsDate(rentalData?.selected_filters?.pickup_date)?.locale(
        mapNamedLocaleToLocale[appNavigator.locale].toLowerCase(),
      ) || null,
      getDayjsDate(rentalData?.selected_filters?.dropoff_date)?.locale(
        mapNamedLocaleToLocale[appNavigator.locale].toLowerCase(),
      ) || null,
    ],
    [
      rentalData?.selected_filters?.pickup_date,
      rentalData?.selected_filters?.dropoff_date,
    ],
  );

  const rangePickerProps = () => ({
    value: geoPickerValue,
    onChange: (newValue) => {
      handleFieldChange({
        pickup_date: formatDateToResponse(newValue[0]),
        dropoff_date: formatDateToResponse(newValue[1]),
      });
    },
    inputLabel: cmsData.whenRidingLabel,
    disabled: !rentalData?.selected_filters?.pickup_place,
    disableInfo: cmsData.datePickerInfoLabel,
    handleClearRangePicker: () => {
      handleFieldChange({
        pickup_date: null,
        dropoff_date: null,
      });
    },
    minDate: getDayjsDate(rentalData?.filters?.min_pickup_date)?.toDate(),
  });
  const filtersProps = () => ({
    vehicleType: {
      options: rentalData?.filters.vehicle_types,
      value: getObjectValue(
        VEHICLE_TYPES,
        rentalData?.selected_filters?.vehicle_type,
      ),
      onChange: (e, newValue) => handleFieldChange({
        vehicle_type_slug: newValue.slug,
        vehicle_make_slug: null,
        vehicle_model_slug: null,
        page: 1,
      }),
      disableClearable: true,
      className: 'GWSAutoComplete',
      label: rentalData?.selected_filters?.vehicle_type?.id
        ? cmsData.vehicleTypeLabel
        : cmsData.allVehicleTypesLabel,

    },
    vehicleMake: {
      options: rentalData?.filters.vehicle_makes,
      value: getObjectValue(
        VEHICLE_MAKES,
        rentalData?.selected_filters?.vehicle_make,
      ),
      onChange: (e, newValue) => handleFieldChange({
        vehicle_type_slug: newValue?.slug ? null : 'motorcycle-rental',
        vehicle_make_slug: newValue?.slug,
        vehicle_model_slug: null,
        page: 1,
      }),
      className: 'GWSAutoComplete',
      label: rentalData?.selected_filters?.vehicle_make?.id
        ? cmsData.vehicleMakeLabel
        : cmsData.allMakesLabel,

    },
    vehicleModel: {
      options: rentalData?.filters.vehicle_models,
      getOptionLabel: (option) => option.name,
      value: getObjectValue(
        VEHICLE_MODELS,
        rentalData?.selected_filters?.vehicle_model,
      ),
      onChange: (e, newValue) => handleFieldChange({
        vehicle_model_slug: newValue?.slug,
        page: 1,
      }),
      className: 'GWSAutoComplete',
      label: rentalData?.selected_filters?.vehicle_model?.id
        ? cmsData.vehicleModelLabel
        : cmsData.allModelsLabel,

    },
  });

  useEffect(() => {
    if (rentalData?.selected_filters?.pickup_place) {
      // Modify this condition to always trigger location fetching if needed
      if (!pickupLocation?.id || locations.pickup.length === 0) {
        if (controller) {
          controller.abort();
        }
        controller = new AbortController();
        getPickupLocations(rentalData?.selected_filters?.pickup_place?.name);
        const params:any = {};
        if (rentalData?.selected_filters.pickup_place?.id === null) {
          params.lat = rentalData?.selected_filters.pickup_place?.location?.lat;
          params.lng = rentalData?.selected_filters.pickup_place?.location?.lng;
        } else {
          params.place_id = rentalData?.selected_filters.pickup_place?.id;
        }
        getDropoffLocatoins(params);
      }
    }
    if (
      typeof rentalData?.selected_filters?.dropoff_location?.id === 'number'
    ) {
      if (!dropoffLocation) {
        if (rentalData?.selected_filters?.pickup_place?.id) {
          getDropoffLocatoins({
            place_id: rentalData?.selected_filters?.pickup_place?.id,
          });
        }
      }
      if (sameDropoff) {
        handleReturnToDifferentLocationClick(undefined, false);
      }
    }
  }, [
    rentalData?.selected_filters?.pickup_place,
    rentalData?.selected_filters?.dropoff_location,
  ]);
  return (
    <StyledBox
      ref={widgetRef}
      pickupSelected={rentalData?.selected_filters?.pickup_place}
      onClick={() => {
        if (document.getElementById('map-info-card')) {
          document.getElementById('map-info-card-colse')?.click();
        }
      }}>
      <Box className="GWSFlexContainer" display="flex" justifyContent="flex-start">
        <Box className="GWSAutoLocationsContainer GWSGridItem">
          <Box className="GWSlocInput">
            <GeoLocationField
              {...pickupFieldProps()}
              rentalData={rentalData} />
          </Box>
          {rentalData?.selected_filters.pickup_place && (
            <Box className="GWSReturnContainer showDESKTOP">
              {sameDropoff ? (
                <StyledFormGroup
                  className="GWSReturn"
                  >
                  <FormControlLabel {...returnToSameLocationProps()} />
                  {hasNoDropoffLocations && (
                    <StyledTooltip
                      enterTouchDelay={0}
                      title={(
                        <Alert severity="error">
                          {cmsData.disableReturnToSameLabel}
                        </Alert>
                      )}>
                      <Button className="GWSRTSLToolTip">&nbsp;</Button>
                    </StyledTooltip>
                  )}
                </StyledFormGroup>
              ) : (
                <GeoLocationField
                  {...dropoffFieldProps()}
                  rentalData={rentalData} />
              )}
            </Box>
          )}
          <Box
            onClick={handleGeoWidgetOpen}
            className="GWSFieldHolder showTABLET"
          />

        </Box>
        <Box className="GWSRangePicker GWSGridItem showTabletAndDesktop">
          <StyledIconedBox
            Icon={RiCalendar2Line}
            size={18}
            disabled={!rentalData?.selected_filters?.pickup_place}>
            <GeoRangePicker {...rangePickerProps()} />
          </StyledIconedBox>
        </Box>
        {!!rentalData?.filters.vehicle_types?.length && (
          <Box className="GWSVehicleType GWSGridItem showDESKTOP ">
            <Box className="fullHeight">
              <FilterAutoComplete {...filtersProps().vehicleType} />
            </Box>
          </Box>
        )}
        {(!!rentalData?.filters.vehicle_makes?.length
          || rentalData?.selected_filters.vehicle_make) && (
          <Box className="GWSVehicleType GWSGridItem showDESKTOP">
            <Box className="fullHeight">
              <FilterAutoComplete {...filtersProps().vehicleMake} />
            </Box>
          </Box>
        )}
        {(!!rentalData?.filters.vehicle_models?.length
          || rentalData?.selected_filters.vehicle_model) && (
          <Box className="GWSVehicleType  GWVModel GWSGridItem showDESKTOP">
            <Box className="fullHeight">
              <FilterAutoComplete {...filtersProps().vehicleModel} />
            </Box>
          </Box>
        )}
      </Box>
      {geoWidgetDialog && (
        <GeoWidgetDialog
          open={geoWidgetDialog}
          onClose={handleGeoWidgetDialogClose}
          handleFieldChange={handleFieldChange}
          rentalData={rentalData}
          pickupFieldProps={pickupFieldProps()}
          dropoffFieldProps={dropoffFieldProps()}
          rangePickerProps={rangePickerProps()}
          returnToSameLocationProps={returnToSameLocationProps()}
        />
      )}
      {openFilterDialog && (
        <GeoFilterDialog
          open={openFilterDialog}
          filtersProps={filtersProps()}
          rentalData={rentalData}
          handleClearVehicleFilters={handleClearVehicleFilters}
          onClose={handleFilterDialogClose}
        />
      )}
    </StyledBox>
  );
};

export { GeoWidgetSection };
